/**
 * System Health cards — Telematics, Order Management, Driver and Fuel, each
 * with a headline figure, key insights and a 7-day trend. Moved verbatim from
 * the Control Centre overview (FleetHubOverview) onto the System Health page;
 * same inputs, same numbers.
 */

import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { Fuel, Package, Users, MapPin, TrendingUp, TrendingDown, Activity, AlertTriangle, ChevronRight, Brain, Rocket, CheckCircle, Award, Calculator, Loader2, RefreshCw, type LucideIcon } from 'lucide-react';
import { type AquariusData, type JigsawData, type OptifleetData, type QargoData } from '../../types/fleetDataHub';
import LineChart from '../LineChart';
import PortalHelpTooltip from '../fleetDataHub/PortalHelpTooltip';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { API_ENDPOINTS } from '../../config/api';
import {
  buildHealthTrendFromFetchedData,
  orderHealthScoreFromCounts,
  type TelematicsActivityDay,
  type OrdersActivityDay,
  type OrderDateSource,
} from '../../utils/systemHealthTrend';
import { useOrdersDailyProjection } from '../../hooks/useOrdersDailyProjection';
import { useOrdersDailySeries } from '../../hooks/useOrdersDailySeries';
import { formatPct, londonDateString } from '../../utils/ordersDailyPresentation';
import { resolveProlongedIdleCount } from '../../utils/countProlongedIdleVehicles';
import {
  buildDailyCompletionRateSeriesFromSeries,
  completionPctForDirection,
  consignmentCountsForDirection,
  londonLastNDaysRange,
  type OrdersRateMode,
} from '../../utils/ordersDailyRates';
import {
  TELEMATICS_AVAILABILITY_FORMULA,
  summarizeTelematicsAggregates,
} from '../../utils/telematicsAvailabilityScore';
import {
  ORDERS_RATE_LINE_COLLECTION,
  ORDERS_RATE_LINE_DELIVERY,
  SECTION_SCORE_KEY,
  AXIS_WEEKDAY_ONLY,
  dayAxisPoint,
  trendBadgeLabel,
  trendFromCountSeries,
  trendFromSeries,
  type HealthTrend,
  type ScorePresentation,
  type SystemHealthDay,
} from '../../utils/systemHealthPresentation';
import { Badge, type BadgeTone } from '../ui/badge';
import type { AnalyticsOverview, DashboardOverview } from '../../types/fleetDataHub';

/** Trend → badge tone (the pill shape and color live in `Badge`). */
const TREND_TONE: Record<HealthTrend, BadgeTone> = {
  up: 'success',
  down: 'destructive',
  updating: 'info',
  stable: 'neutral',
};

/** Qargo payload may expose active_orders in live analytics even if the typed export shape uses in_progress_orders. */
type QargoOverviewData = QargoData & { active_orders?: number };

const insightToneClass = (tone?: 'default' | 'success' | 'warning' | 'danger') =>
  tone === 'success' ? 'text-emerald-400'
    : tone === 'warning' ? 'text-amber-400'
      : tone === 'danger' ? 'text-red-400'
        : 'text-white';

/** Rate % plus completed/total consignment counts (neutral — no mid-day quality colour). */
function orderRateInsightValue(
  pct: number | null,
  hasWork: boolean,
  counts: { completed: number; total: number },
): ReactNode {
  if (!hasWork) return '—';
  return (
    <>
      {formatPct(pct)}{' '}
      <span className="text-gray-400 font-normal">
        (<span className="font-semibold tabular-nums text-white">
          {counts.completed.toLocaleString()}/{counts.total.toLocaleString()}
        </span>
        {' '}consignments)
      </span>
    </>
  );
}

/** The slice of the Control Centre data FleetDataHub already loads that the cards read. */
export interface SystemHealthCardsProps {
  analyticsOverview: AnalyticsOverview | null;
  dashboardOverview: DashboardOverview | null;
  onFetchData: () => void;
  isLoading: boolean;
  useMockData?: boolean;
  /** Fallback data from individual API fetches (used when analyticsOverview doesn't contain nested data) */
  jigsawData?: JigsawData | null;
  optifleetData?: OptifleetData | null;
  aquariusData?: AquariusData | null;
  /** Accurate order stats computed from the full export data (all_orders) - preferred over analytics engine sample data */
  orderStats?: { total: number; cancelled: number; completed: number; active: number; pending: number } | null;
  samsaraData?: OptifleetData | null;
  telematicsTotals?: {
    totalVehicles: number;
    alertCount: number;
    idleVehicles: number;
    prolongedIdleVehicles?: number;
    movingVehicles?: number;
    stoppedVehicles?: number;
    offlineVehicles?: number;
    vehiclesWithGps?: number;
    positionCount?: number;
    availabilityScore?: number;
    /** False when there are no in-use vehicles to score (availabilityScore is a sentinel 0). */
    scorable?: boolean;
  } | null;
  driverStatsForAlerts?: { violations: number; driverCount: number; compliancePct: number } | null;
  fuelStatsForAlerts?: { totalLitres: number; totalCost: number; transactionCount: number } | null;
  /** True when Palletways is the order system (orders_daily rates). */
  ordersDailyEnabled?: boolean;
  /** Already-fetched orders (last lookback) — used to backfill 7-day health charts */
  ordersForHealthTrend?: OrderDateSource[] | null;
}

export default function SystemHealthCards({
  analyticsOverview,
  dashboardOverview,
  onFetchData,
  isLoading,
  useMockData = false,
  jigsawData: jigsawDataProp,
  optifleetData: optifleetDataProp,
  aquariusData: aquariusDataProp,
  orderStats,
  telematicsTotals,
  samsaraData: samsaraDataProp,
  driverStatsForAlerts,
  fuelStatsForAlerts,
  ordersForHealthTrend,
  ordersDailyEnabled = false,
}: SystemHealthCardsProps) {
  const overall = analyticsOverview?.overall;
  const [ordersRateMode, setOrdersRateMode] = useState<OrdersRateMode>('delivery');
  const [healthHistory, setHealthHistory] = useState<SystemHealthDay[]>([]);
  const [telematicsActivity, setTelematicsActivity] = useState<TelematicsActivityDay[]>([]);
  const [ordersActivity, setOrdersActivity] = useState<OrdersActivityDay[]>([]);
  const [telematicsFleetSize, setTelematicsFleetSize] = useState(0);
  const lastHealthUpsertKeyRef = useRef<string>('');
  const lastBackfillKeyRef = useRef<string>('');

  // Fresh London YMD each render so overnight wallboards do not stick on yesterday.
  const londonToday = londonDateString();
  // Demo toggle always uses the Palletways rates wallboard (same as Orders tab),
  // even if the parent forgot to flip ordersDailyEnabled.
  const showOrdersDailyRates = ordersDailyEnabled || useMockData;
  // Both reads below are served from stored figures, never computed on page
  // load: today's rates from the stored Today snapshot (single day + backlog,
  // the same read as the Orders tab's Today chip), the 7-day trend from the
  // stored per-day series. A 7-day rows read is not stored and computed live.
  const ordersTrendRange = useMemo(
    () => londonLastNDaysRange(7),
    [londonToday],
  );
  const {
    summary: ordersDailySummary,
    rows: ordersDailyRows,
    error: ordersDailyError,
    loading: ordersDailyLoading,
  } = useOrdersDailyProjection(
    londonToday,
    londonToday,
    { enabled: showOrdersDailyRates, demo: useMockData, includeBacklog: true },
  );
  const { days: ordersTrendDays } = useOrdersDailySeries(
    ordersTrendRange.startDate,
    ordersTrendRange.endDate,
    { enabled: showOrdersDailyRates, demo: useMockData },
  );
  // Section refs for scrolling
  const sectionRefs = useRef<{
    fuel?: HTMLDivElement | null;
    orders?: HTMLDivElement | null;
    drivers?: HTMLDivElement | null;
    gps?: HTMLDivElement | null;
  }>({});
  const healthCardsRootRef = useRef<HTMLDivElement | null>(null);
  // Load persisted System Health history + Iceberg GPS activity for trend charts
  useEffect(() => {
    if (useMockData) {
      setHealthHistory([]);
      setTelematicsActivity([]);
      setOrdersActivity([]);
      setTelematicsFleetSize(0);
      return;
    }
    let cancelled = false;
    void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.systemHealthDaily(7))
      .then(async (res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.days || !Array.isArray(json.days)) return;
        setHealthHistory(
          json.days.map((d: SystemHealthDay) => ({
            date: String(d.date),
            telematics: Number(d.telematics) || 0,
            orders: Number(d.orders) || 0,
            drivers: Number(d.drivers) || 0,
            fuel: Number(d.fuel) || 0,
          })),
        );
      })
      .catch(() => {
        /* non-blocking — charts stay empty until history exists */
      });

    void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.vehicles.activityByDay(7))
      .then(async (res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.series || !Array.isArray(json.series)) return;
        setTelematicsActivity(
          json.series.map((d: { date?: string; positions?: number; active_vehicles?: number }) => ({
            date: String(d.date ?? ''),
            positions: Number(d.positions) || 0,
            active_vehicles: Number(d.active_vehicles) || 0,
          })),
        );
        setTelematicsFleetSize(Number(json.fleet_size) || 0);
      })
      .catch(() => {
        /* non-blocking — telematics chart falls back to live snapshot */
      });

    void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.orders.activityByDay(7))
      .then(async (res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.series || !Array.isArray(json.series)) return;
        const series = json.series.map((d: { date?: string; orders?: number; completed?: number; cancelled?: number; open?: number }) => ({
          date: String(d.date ?? ''),
          orders: Number(d.orders) || 0,
          completed: Number(d.completed) || 0,
          cancelled: Number(d.cancelled) || 0,
          open: d.open != null ? Number(d.open) || 0 : undefined,
        }));
        // The endpoint zero-fills every day in the window, so a successful
        // response is never an empty array — an org whose orders live in
        // neither Iceberg truth table yet (not backfilled, or a source this
        // rollup doesn't cover) would otherwise present as an all-zero
        // series and win over buildHealthTrendFromFetchedData's client-list
        // fallback, flattening the whole trend to zero. Only trust the
        // rollup once it actually contains a non-zero day (mirrors
        // OrdersSection's identical guard on its own activity-by-day read).
        if (series.some((d: OrdersActivityDay) => d.orders > 0)) {
          setOrdersActivity(series);
        }
      })
      .catch(() => {
        /* non-blocking — orders sparkline falls back to list bucketing */
      });

    return () => {
      cancelled = true;
    };
  }, [useMockData]);

  // Fade-in System Health cards on scroll into view (same pattern as ZOE "Your agents").
  useEffect(() => {
    const root = healthCardsRootRef.current;
    if (!root) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const cards = Array.from(root.querySelectorAll<HTMLElement>('.zoe-agent-card'));
    if (cards.length === 0) return;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      for (const el of cards) el.classList.add('zoe-inview');
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          (e.target as HTMLElement).classList.add('zoe-inview');
          obs.unobserve(e.target);
        }
      },
      { threshold: 0.15 },
    );

    for (const el of cards) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Calculate section scores from real API/export data only (no hardcoded defaults)
  const fuelScore = overall?.fleet_health_score != null ? Math.round(overall.fleet_health_score) : 0;

  const qargoLocal = analyticsOverview?.apis?.qargo?.data as QargoOverviewData | undefined;
  const orderVolume = orderStats?.total ?? overall?.total_orders ?? 0;
  const ordersScore =
    orderVolume && (qargoLocal || orderStats)
      ? orderHealthScoreFromCounts(
          orderVolume,
          orderStats?.completed ?? qargoLocal?.completed_orders ?? 0,
          orderStats?.cancelled ?? qargoLocal?.cancelled_orders ?? 0,
        )
      : 0;

  const complianceForDrivers =
    driverStatsForAlerts?.compliancePct
    ?? overall?.compliance_score
    ?? 0;
  /** Control Centre Driver headline = real compliance %, not a transformed health score. */
  const driversScore = Number.isFinite(complianceForDrivers)
    ? Math.max(0, Math.min(100, Math.round(complianceForDrivers)))
    : 0;
  const driversCountInsight =
    driverStatsForAlerts?.driverCount
    ?? overall?.total_drivers
    ?? 0;
  const driversViolationsInsight = driverStatsForAlerts?.violations ?? 0;

  // Telematics score — prefer live GPS totals (Samsara/Optifleet/Supatrak), not Iceberg fleet_health_score
  // which stays 0 until positions are ingested to the truth layer.
  const liveTelematicsVehicles =
    telematicsTotals?.totalVehicles
    ?? samsaraDataProp?.vehicle_count
    ?? samsaraDataProp?.vehicles?.length
    ?? optifleetDataProp?.vehicle_count
    ?? optifleetDataProp?.vehicles?.length
    ?? 0;

  const liveTelematicsAlerts =
    telematicsTotals?.alertCount ?? optifleetDataProp?.alert_count ?? samsaraDataProp?.alert_count ?? 0;

  const liveTelematicsIdle =
    telematicsTotals?.idleVehicles ?? optifleetDataProp?.idle_vehicles ?? samsaraDataProp?.idle_vehicles ?? 0;

  const liveTelematicsProlongedIdle = resolveProlongedIdleCount(
    telematicsTotals,
    samsaraDataProp,
    optifleetDataProp,
  );

  const liveTelematicsMoving =
    telematicsTotals?.movingVehicles ?? optifleetDataProp?.moving_vehicles ?? samsaraDataProp?.moving_vehicles ?? 0;

  const liveTelematicsStopped =
    telematicsTotals?.stoppedVehicles ?? optifleetDataProp?.stopped_vehicles ?? samsaraDataProp?.stopped_vehicles ?? 0;

  const liveTelematicsOffline =
    telematicsTotals?.offlineVehicles ?? optifleetDataProp?.offline_vehicles ?? samsaraDataProp?.offline_vehicles ?? 0;

  const liveTelematicsPositionCount =
    telematicsTotals?.positionCount
    ?? optifleetDataProp?.position_count
    ?? samsaraDataProp?.position_count
    ?? 0;

  // Prefer totals computed with the same age filter + in-use GPS rules as Telematics.
  const gpsAggregateSummary = summarizeTelematicsAggregates({
    totalVehicles: liveTelematicsVehicles,
    movingVehicles: liveTelematicsMoving,
    idleVehicles: liveTelematicsIdle,
    stoppedVehicles: liveTelematicsStopped,
    offlineVehicles: liveTelematicsOffline,
    alertCount: liveTelematicsAlerts,
    positionCount: liveTelematicsPositionCount,
  });
  const gpsScore = Math.round(
    telematicsTotals?.availabilityScore ?? gpsAggregateSummary.availabilityScore,
  );
  const gpsHasTelematics =
    telematicsTotals != null || optifleetDataProp != null || samsaraDataProp != null;
  // availabilityScore is a sentinel 0 when nothing is in use (fleet parked) or the
  // aggregate payload carries no moving/idle/stopped breakdown — show "—", not a
  // red 0% alarm. Trust the parent's `scorable` when it computed the score.
  const gpsScorable =
    telematicsTotals?.availabilityScore != null
      ? telematicsTotals.scorable !== false
      : gpsAggregateSummary.scorable;

  const prolongedIdlePctRatio =
    liveTelematicsVehicles > 0 ? liveTelematicsProlongedIdle / liveTelematicsVehicles : 0;
  const prolongedIdlePctInsight = Math.round(prolongedIdlePctRatio * 100);

  const todayOrdersDailyRows = useMemo(
    () => (showOrdersDailyRates
      ? ordersDailyRows.filter((r) => r.grain_date === londonToday)
      : []),
    [showOrdersDailyRates, ordersDailyRows, londonToday],
  );
  const deliveryRateToday = showOrdersDailyRates
    ? completionPctForDirection(todayOrdersDailyRows, 'delivery')
    : null;
  const collectionRateToday = showOrdersDailyRates
    ? completionPctForDirection(todayOrdersDailyRows, 'collection')
    : null;
  const deliveryCountsToday = useMemo(
    () => consignmentCountsForDirection(todayOrdersDailyRows, 'delivery'),
    [todayOrdersDailyRows],
  );
  const collectionCountsToday = useMemo(
    () => consignmentCountsForDirection(todayOrdersDailyRows, 'collection'),
    [todayOrdersDailyRows],
  );
  const deliveryHasWork = deliveryRateToday != null;
  const collectionHasWork = collectionRateToday != null;
  const activeRateToday = ordersRateMode === 'delivery' ? deliveryRateToday : collectionRateToday;
  const activeRateHasWork = ordersRateMode === 'delivery' ? deliveryHasWork : collectionHasWork;
  const collectionsProvisional =
    ordersDailySummary?.data_quality?.collection_date_semantics === 'provisional';

  const deliveryRateSeries = useMemo(
    () => (showOrdersDailyRates
      ? buildDailyCompletionRateSeriesFromSeries(ordersTrendDays, 'delivery', { days: 7, todayDate: londonToday })
      : []),
    [showOrdersDailyRates, ordersTrendDays, londonToday],
  );
  const collectionRateSeries = useMemo(
    () => (showOrdersDailyRates
      ? buildDailyCompletionRateSeriesFromSeries(ordersTrendDays, 'collection', { days: 7, todayDate: londonToday })
      : []),
    [showOrdersDailyRates, ordersTrendDays, londonToday],
  );
  const activeRateSeries = ordersRateMode === 'delivery' ? deliveryRateSeries : collectionRateSeries;
  const ordersRateTrendData = useMemo(
    () => activeRateSeries.map((p) => ({
      ...(p.date ? dayAxisPoint(p.date) : { label: p.label }),
      value: Math.round(p.value),
      date: p.date,
    })),
    [activeRateSeries],
  );
  // Delivery/collection rates keep moving all day — never show Improving /
  // Declining on this card (morning rates look artificially low). Chart still
  // includes today; the badge stays neutral until we have a deliberate EOD rule.
  const ordersRateTrend = 'updating' as const;
  /** Big number on the Order Management card — rate for the selected mode, not health score. */
  const ordersDisplayScore = showOrdersDailyRates
    ? (activeRateToday != null ? Math.round(activeRateToday) : null)
    : ordersScore;
  const ordersRateDaysOmitted = showOrdersDailyRates && activeRateSeries.length > 0 && activeRateSeries.length < 7;

  const fuelFillUps =
    fuelStatsForAlerts?.transactionCount
    ?? jigsawDataProp?.transaction_count
    ?? overall?.total_fuel_transactions
    ?? 0;
  const fuelLitresInsight =
    fuelStatsForAlerts?.totalLitres
    ?? jigsawDataProp?.total_fuel_litres
    ?? 0;
  const fuelSpendInsight =
    fuelStatsForAlerts?.totalCost
    ?? jigsawDataProp?.total_fuel_cost
    ?? 0;
  const fuelStatsPresent =
    fuelStatsForAlerts != null
    || jigsawDataProp != null
    || overall?.total_fuel_transactions != null;

  const fuelFillUpTrendData = useMemo(() => {
    const daily = (jigsawDataProp?.daily_transactions ?? null) as Record<string, number> | null;
    if (!daily) return [] as Array<{ label: string; weekday?: string; value: number; date?: string }>;
    return Object.entries(daily)
      .map(([raw, count]) => {
        const date = String(raw).replace(' ', 'T').split('T')[0];
        return { date, value: Number(count) || 0 };
      })
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.date))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-7)
      .map((d) => ({
        ...dayAxisPoint(d.date),
        value: d.value,
        date: d.date,
      }));
  }, [jigsawDataProp?.daily_transactions]);

  const chartDays = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const live: SystemHealthDay = {
      date: today,
      telematics: gpsScore,
      orders: ordersScore,
      drivers: driversScore,
      fuel: fuelScore,
    };
    const liveFleet =
      telematicsTotals?.totalVehicles
      ?? telematicsFleetSize
      ?? 0;
    const fromFetched = buildHealthTrendFromFetchedData({
      days: 7,
      live,
      orders: ordersForHealthTrend,
      ordersActivity,
      dailyFuelTransactions: jigsawDataProp?.daily_transactions ?? null,
      driverDetails: aquariusDataProp?.driver_details ?? null,
      driverCount: aquariusDataProp?.driver_count ?? driverStatsForAlerts?.driverCount,
      telematicsActivity,
      telematicsFleetSize: Math.max(telematicsFleetSize, liveFleet),
    });
    const persisted = new Map(healthHistory.filter((d) => d?.date).map((d) => [d.date, d]));
    const hasGpsHistory = telematicsActivity.some(
      (d) => (d.positions ?? 0) > 0 || (d.active_vehicles ?? 0) > 0,
    );
    // Prefer persisted snapshots when present; always overlay today with live scores.
    // Iceberg GPS coverage wins for past telematics so flat live copies get replaced.
    return fromFetched.map((computed) => {
      if (computed.date === today) return live;
      const prior = persisted.get(computed.date);
      if (!prior) return computed;
      if (hasGpsHistory) {
        return { ...prior, telematics: computed.telematics };
      }
      return prior;
    });
  }, [
    healthHistory,
    gpsScore,
    ordersScore,
    driversScore,
    fuelScore,
    ordersForHealthTrend,
    jigsawDataProp,
    aquariusDataProp,
    driverStatsForAlerts,
    telematicsActivity,
    ordersActivity,
    telematicsFleetSize,
    telematicsTotals,
  ]);

  // Persist today's live scores + backfill any missing calendar days from fetched data
  useEffect(() => {
    if (useMockData) return;
    if (!analyticsOverview && !dashboardOverview) return;
    if (chartDays.length === 0) return;

    const today = new Date().toISOString().slice(0, 10);
    const upsertKey = [gpsScore, ordersScore, driversScore, fuelScore].join('|');
    const backfillKey = chartDays
      .map((d) => `${d.date}:${d.telematics}:${d.orders}:${d.drivers}:${d.fuel}`)
      .join('|');
    const shouldUpsertToday = lastHealthUpsertKeyRef.current !== upsertKey;
    const persistedByDate = new Map(healthHistory.map((d) => [d.date, d]));
    const daysNeedingWrite = chartDays.filter((d) => {
      if (d.date === today) return false;
      const prior = persistedByDate.get(d.date);
      if (!prior) return true;
      // Refresh stored telematics when Iceberg coverage differs from an old flat snapshot
      return prior.telematics !== d.telematics
        || prior.orders !== d.orders
        || prior.drivers !== d.drivers
        || prior.fuel !== d.fuel;
    });
    const shouldBackfill =
      daysNeedingWrite.length > 0 && lastBackfillKeyRef.current !== backfillKey;

    if (!shouldUpsertToday && !shouldBackfill) return;
    lastHealthUpsertKeyRef.current = upsertKey;
    if (shouldBackfill) lastBackfillKeyRef.current = backfillKey;

    const daysToWrite = [
      ...(shouldUpsertToday
        ? [{ date: today, telematics: gpsScore, orders: ordersScore, drivers: driversScore, fuel: fuelScore }]
        : []),
      ...(shouldBackfill ? daysNeedingWrite : []),
    ];
    if (daysToWrite.length === 0) return;

    let cancelled = false;
    void (async () => {
      let lastJson: { days?: SystemHealthDay[] } | null = null;
      for (const day of daysToWrite) {
        try {
          const res = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.systemHealthDailyWrite(), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: day.date,
              telematics: day.telematics,
              orders: day.orders,
              drivers: day.drivers,
              fuel: day.fuel,
            }),
          });
          if (res.ok) lastJson = await res.json();
        } catch {
          /* best-effort */
        }
        if (cancelled) return;
      }
      if (cancelled || !lastJson?.days || !Array.isArray(lastJson.days)) return;
      setHealthHistory(
        lastJson.days.map((d: SystemHealthDay) => ({
          date: String(d.date),
          telematics: Number(d.telematics) || 0,
          orders: Number(d.orders) || 0,
          drivers: Number(d.drivers) || 0,
          fuel: Number(d.fuel) || 0,
        })),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [
    useMockData,
    analyticsOverview,
    dashboardOverview,
    chartDays,
    healthHistory,
    gpsScore,
    ordersScore,
    driversScore,
    fuelScore,
  ]);

  const trendPointsFor = (sectionId: 'fuel' | 'orders' | 'drivers' | 'gps') => {
    const key = SECTION_SCORE_KEY[sectionId];
    return chartDays.map((d) => ({
      ...dayAxisPoint(d.date),
      value: d[key],
      date: d.date,
    }));
  };

  // Directional badge from 7-day chart slope (not absolute score bands)
  const gpsTrend = trendFromSeries(chartDays.map((d) => d.telematics));
  const ordersTrend = trendFromSeries(chartDays.map((d) => d.orders));
  const driversTrend = trendFromSeries(chartDays.map((d) => d.drivers));
  const fuelTrend = trendFromCountSeries(fuelFillUpTrendData.map((d) => d.value));

  // If no data, show fetch prompt
  if (!analyticsOverview && !dashboardOverview) {
    return (
      <div className="text-center py-16">
        <div className="relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-blue-500/10 rounded-full animate-pulse"></div>
          </div>
          <Brain className="w-20 h-20 text-blue-400 mx-auto mb-6 relative z-10 animate-bounce" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Fleet Intelligence Awaits</h3>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          Power up your control centre with real-time analytics and operational insights
        </p>
        <Button
          type="button"
          variant="default"
          size="lg"
          onClick={onFetchData}
          disabled={isLoading}
          className="h-auto gap-3 rounded-xl bg-gradient-to-r from-primary to-brand px-8 py-4 text-white shadow-lg transition-all hover:from-primary/90 hover:to-brand/90 hover:shadow-primary/50 transform hover:scale-105"
        >
          <Rocket className={`w-6 h-6 ${isLoading ? 'animate-spin' : ''}`} aria-hidden />
          <span className="font-semibold">{isLoading ? 'Initializing...' : 'Activate Control Centre'}</span>
        </Button>
      </div>
    );
  }

  // Section boxes configuration
  const sections: Array<{
    id: 'fuel' | 'orders' | 'drivers' | 'gps';
    title: string;
    icon: LucideIcon;
    color: string;
    score: number;
    /** percent (default) or count — count omits the % suffix and quality bands. */
    scoreUnit?: 'percent' | 'count';
    /**
     * How to present the headline figure badge:
     * - judgment: Excellent / Good / Attention (health scores)
     * - label: neutral copy via scoreBadge (live delivery/collection rates)
     * - count: absolute counts (fill-ups)
     */
    scorePresentation?: ScorePresentation;
    /** Badge label when scorePresentation is label or count. */
    scoreBadge?: string;
    /** When true, show — instead of 0% (no consignments in the selected rate mode). */
    scoreEmpty?: boolean;
    /** Badge copy when scoreEmpty (defaults to "No work today"). */
    scoreEmptyLabel?: string;
    /** Initial fetch in flight — avoid treating empty as "no work". */
    scoreLoading?: boolean;
    /** Hide the 0–100 progress bar (e.g. absolute counts). */
    hideScoreBar?: boolean;
    trend: HealthTrend;
    trendData: Array<{ label: string; weekday?: string; value: number; date?: string }>;
    textColor: string;
    description: string;
    formula: string;
    insights: Array<{ label: string; value: ReactNode; tone?: 'default' | 'success' | 'warning' | 'danger' }>;
  }> = [
    {
      id: 'gps' as const,
      title: 'Telematics',
      icon: MapPin,
      color: 'blue',
      score: gpsScore,
      // Don't show a red 0% alarm when there is simply nothing in use to score
      // (fleet parked overnight, or an aggregate feed with no status breakdown).
      scoreEmpty: !gpsHasTelematics || !gpsScorable,
      scoreEmptyLabel: !gpsHasTelematics ? 'No telematics data' : 'No vehicles in use',
      trend: gpsTrend,
      trendData: trendPointsFor('gps'),
      textColor: 'text-blue-400',
      description: 'GPS availability for in-use vehicles',
      formula: `${TELEMATICS_AVAILABILITY_FORMULA} Last 7 days chart: share of fleet reporting GPS that day (from ingested positions).`,
      insights: [
        { label: 'Vehicles tracked', value: liveTelematicsVehicles.toLocaleString(), tone: 'default' },
        {
          label: 'Idling ≥15 min',
          value: liveTelematicsVehicles > 0
            ? `${liveTelematicsProlongedIdle.toLocaleString()} (${prolongedIdlePctInsight}%)`
            : liveTelematicsProlongedIdle.toLocaleString(),
          tone: liveTelematicsProlongedIdle > 0 ? 'warning' : 'default',
        },
        {
          label: 'Offline now',
          value: liveTelematicsOffline.toLocaleString(),
          tone: liveTelematicsOffline > 0 ? 'warning' : 'default',
        },
      ]
    },
    {
      id: 'orders' as const,
      title: 'Order Management',
      icon: Package,
      color: 'indigo',
      score: ordersDisplayScore ?? 0,
      // Live delivery/collection % — label what the number is; do not judge Excellent/Attention mid-day.
      scorePresentation: showOrdersDailyRates ? ('label' as const) : ('judgment' as const),
      scoreBadge: showOrdersDailyRates
        ? (ordersRateMode === 'delivery' ? 'Delivery rate' : 'Collection rate')
        : undefined,
      scoreEmpty: showOrdersDailyRates && !ordersDailyLoading && !activeRateHasWork,
      scoreLoading: showOrdersDailyRates && ordersDailyLoading,
      trend: showOrdersDailyRates ? ordersRateTrend : ordersTrend,
      trendData: showOrdersDailyRates ? ordersRateTrendData : trendPointsFor('orders'),
      textColor: 'text-indigo-400',
      description: showOrdersDailyRates
        ? (useMockData
          ? 'Demo fixtures — same Delivery / Collection rates as the Orders tab wallboard. Toggle Delivery or Collection on the chart header.'
          : 'Headline and chart are Palletways Delivery / Collection rates (consignment grain — same as the Orders tab gauges). Toggle Delivery or Collection on the chart header.')
        : 'Realistic order health based on completion and cancellation rates',
      formula: showOrdersDailyRates
        ? 'Rate = completed consignments ÷ total consignments in that direction for the day. Deliveries are dated by due date, collections by the date entered. Days with no consignments are omitted from the chart.'
        : 'Score = 55 (baseline) + completion bonus (60-90%: +15 to +30) + cancellation bonus (≤10%: +5 to +10) + volume bonus (5k+: +3 to +5). Range: 50-100.',
      insights: showOrdersDailyRates
        ? ordersDailyLoading
          ? [
              { label: 'Delivery rate', value: '…', tone: 'default' as const },
              { label: 'Collection rate', value: '…', tone: 'default' as const },
            ]
          : [
            {
              label: 'Delivery rate',
              // Colour lives on XX/YY only; keep the % white.
              value: orderRateInsightValue(deliveryRateToday, deliveryHasWork, deliveryCountsToday),
              tone: 'default' as const,
            },
            {
              label: 'Collection rate',
              value: orderRateInsightValue(collectionRateToday, collectionHasWork, collectionCountsToday),
              tone: 'default' as const,
            },
          ]
        : [
            {
              label: 'Orders (period)',
              value: orderVolume > 0 ? orderVolume.toLocaleString() : '—',
              tone: 'default' as const,
            },
            {
              label: 'Health score',
              value: orderVolume > 0 ? `${ordersScore}%` : '—',
              tone:
                ordersScore >= 80
                  ? ('success' as const)
                  : ordersScore >= 60
                    ? ('warning' as const)
                    : orderVolume > 0
                      ? ('danger' as const)
                      : ('default' as const),
            },
          ],
    },
    {
      id: 'drivers' as const,
      title: 'Driver',
      icon: Users,
      color: driversScore >= 75 ? 'green' : driversScore >= 50 ? 'yellow' : 'red',
      score: driversScore,
      scoreEmpty: driversCountInsight <= 0,
      scoreEmptyLabel: 'No drivers',
      trend: driversTrend,
      trendData: trendPointsFor('drivers'),
      textColor: driversScore >= 75 ? 'text-green-400' : driversScore >= 50 ? 'text-yellow-400' : 'text-red-400',
      description: 'Share of drivers with no hours/tacho violations in the selected Control Centre period.',
      formula: 'Compliance % = drivers with zero violations in the period ÷ active drivers. Chart uses the same compliance idea for past days when violation dates are available.',
      insights: [
        {
          label: 'Active drivers',
          value: driversCountInsight > 0 ? driversCountInsight.toLocaleString() : '—',
          tone: 'default',
        },
        {
          label: 'Violations',
          value: driversCountInsight > 0 || driversViolationsInsight > 0
            ? driversViolationsInsight.toLocaleString()
            : '—',
          tone: driversViolationsInsight > 10
            ? 'danger'
            : driversViolationsInsight > 0
              ? 'warning'
              : 'success',
        },
      ],
    },
    {
      id: 'fuel' as const,
      title: 'Fuel',
      icon: Fuel,
      color: 'orange',
      score: fuelFillUps,
      scoreUnit: 'count' as const,
      scorePresentation: 'count' as const,
      scoreBadge: 'Fill-ups',
      scoreEmpty: !fuelStatsPresent || (fuelFillUps === 0 && fuelLitresInsight === 0),
      scoreEmptyLabel: 'No fill-ups',
      hideScoreBar: true,
      trend: fuelTrend,
      trendData: fuelFillUpTrendData,
      textColor: 'text-orange-400',
      description: 'Fill-up count for the selected Control Centre period, with litres purchased and spend.',
      formula: 'Headline = number of fuel transactions (fill-ups). Chart = daily fill-up counts when available.',
      insights: [
        {
          label: 'Litres',
          value: fuelStatsPresent
            ? fuelLitresInsight.toLocaleString(undefined, { maximumFractionDigits: 0 })
            : '—',
          tone: 'default' as const,
        },
        {
          label: 'Spend',
          value: fuelStatsPresent
            ? `£${fuelSpendInsight.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            : '—',
          tone: 'default' as const,
        },
      ],
    },
  ];

  return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700/50 border-l-4 border-l-blue-400">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Health scores</h3>
          </div>
        </div>
        <div className="p-4">
          <div ref={healthCardsRootRef} className="zoe-agent-grid grid grid-cols-1 lg:grid-cols-2 gap-6">
            {sections.map((section) => {
              const Icon = section.icon;
              const presentation: ScorePresentation =
                section.scorePresentation
                ?? (section.scoreUnit === 'count' ? 'count' : 'judgment');
              const TrendIcon =
                section.trend === 'up' ? TrendingUp
                : section.trend === 'down' ? TrendingDown
                : section.trend === 'updating' ? RefreshCw
                : Activity;
              const scoreBadge: { tone: BadgeTone; content: ReactNode } =
                section.scoreLoading
                  ? { tone: 'neutral', content: <><Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden /> Loading</> }
                  : section.scoreEmpty
                  ? { tone: 'neutral', content: <>{section.scoreEmptyLabel ?? 'No work today'}</> }
                  : presentation === 'label' || presentation === 'count'
                  ? { tone: 'neutral', content: <>{section.scoreBadge ?? (presentation === 'count' ? 'Count' : 'Rate')}</> }
                  : section.score >= 80
                  ? { tone: 'success', content: <><Award className="h-3 w-3 shrink-0" aria-hidden /> EXCELLENT</> }
                  : section.score >= 60
                  ? { tone: 'warning', content: <><CheckCircle className="h-3 w-3 shrink-0" aria-hidden /> GOOD</> }
                  : { tone: 'destructive', content: <><AlertTriangle className="h-3 w-3 shrink-0" aria-hidden /> ATTENTION</> };

              return (
                <div
                  key={section.id}
                  ref={el => sectionRefs.current[section.id] = el}
                  className="zoe-agent-card group relative overflow-hidden bg-gray-900/55 rounded-2xl p-6 border border-gray-700/60 shadow-xl hover:border-gray-600 transition-all duration-300"
                >
              <div className="relative">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-white/10 rounded-xl group-hover:scale-110 transition-transform">
                      <Icon className={`w-6 h-6 ${section.textColor}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{section.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge tone={TREND_TONE[section.trend]} icon={TrendIcon}>
                          {trendBadgeLabel(section.trend)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <PortalHelpTooltip iconClassName={`w-5 h-5 ${section.textColor} cursor-help hover:scale-110 transition-transform`}>
                    <div className="text-xs text-white font-semibold mb-2 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      {section.id === 'orders' && showOrdersDailyRates
                        ? 'Rate calculation'
                        : presentation === 'count'
                          ? 'How this figure is counted'
                          : presentation === 'label'
                            ? 'Rate calculation'
                            : 'Score Calculation'}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed mb-2 break-words">{section.description}</p>
                    <div className="bg-gray-800/50 rounded-lg p-3 mb-3 border border-gray-700/50">
                      <div className="text-xs text-blue-300 font-mono leading-relaxed break-words whitespace-pre-wrap">
                        {section.formula}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <ChevronRight className="w-3 h-3" />
                      Click card to view detailed {section.title.toLowerCase()}
                    </div>
                  </PortalHelpTooltip>
                </div>

                {/* Score + Key Insights (side by side) */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-38">
                    <div className="text-5xl font-black text-white mb-2">
                      {section.scoreLoading || section.scoreEmpty ? (
                        <span className="text-gray-500">—</span>
                      ) : presentation === 'count' ? (
                        <span className="tabular-nums">
                          {section.score.toLocaleString()}
                        </span>
                      ) : (
                        <>
                          {section.score}
                          <span className="text-2xl text-gray-400 font-normal">%</span>
                        </>
                      )}
                    </div>
                    <Badge tone={scoreBadge.tone}>
                      {scoreBadge.content}
                    </Badge>
                    {!section.hideScoreBar && (
                      <div className="w-full bg-gray-700/50 rounded-full h-2 mt-3">
                        <div
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            section.scoreLoading || section.scoreEmpty ? 'bg-gray-600' :
                            presentation === 'label' ? 'bg-gradient-to-r from-indigo-500 to-sky-400' :
                            section.score >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                            section.score >= 60 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' :
                            'bg-gradient-to-r from-red-500 to-rose-400'
                          }`}
                          style={{ width: `${section.scoreLoading || section.scoreEmpty ? 0 : section.score}%` }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 rounded-lg border border-white/10 bg-black/20 px-3.5 py-2.5 overflow-hidden">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                      Key Insights
                    </div>
                    {section.id === 'orders' && showOrdersDailyRates && ordersDailyError && (
                      <p className="text-xs text-amber-300 mb-2 leading-snug">
                        {ordersDailyError}
                      </p>
                    )}
                    <div className="divide-y divide-gray-700/70">
                      {section.insights.map((insight, idx) => (
                        <div key={idx} className="flex items-start justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                          <span className="text-xs text-gray-400 leading-snug min-w-0 flex-1 pr-1">
                            {insight.label}
                          </span>
                          <span className={`text-xs font-semibold text-right min-w-0 max-w-58% break-words leading-snug ${insightToneClass(insight.tone)}`}>
                            {insight.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="w-full mb-4 rounded-lg border border-white/5 px-2 pt-2 pb-1" style={{ height: section.id === 'orders' && showOrdersDailyRates ? '248px' : '210px' }}>
                  <div className="flex items-center justify-between gap-2 mb-1 px-1 min-h-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 truncate">
                      {section.id === 'orders' && showOrdersDailyRates
                        ? (ordersRateMode === 'delivery'
                          ? 'Delivery rate — last 7 days'
                          : 'Collection rate — last 7 days')
                        : ({
                            fuel: 'Fill-ups — Last 7 Days',
                            orders: 'Orders Delivered — Last 7 Days',
                            drivers: 'Driver Compliance — Last 7 Days',
                            gps: 'GPS Coverage — Last 7 Days',
                          } as const)[section.id]}
                    </p>
                    {section.id === 'orders' && showOrdersDailyRates && (
                      <div
                        className="inline-flex shrink-0 rounded-md border border-white/10 bg-black/30 p-0.5"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="group"
                        aria-label="Delivery or collection rate"
                      >
                        {([
                          { id: 'delivery' as const, label: 'Delivery' },
                          { id: 'collection' as const, label: 'Collection' },
                        ]).map((opt) => (
                          <Button
                            key={opt.id}
                            type="button"
                            variant={ordersRateMode === opt.id ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setOrdersRateMode(opt.id)}
                            className={`h-auto rounded px-2 py-0.5 text-xs font-semibold ${
                              ordersRateMode === opt.id
                                ? 'bg-primary/40 text-foreground'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                  {section.trendData.length >= 1 ? (
                    <div style={{ height: section.id === 'orders' && showOrdersDailyRates ? '155px' : '175px' }}>
                      <LineChart
                        data={section.trendData}
                        formatXLabel={AXIS_WEEKDAY_ONLY}
                        heightClass="h-full"
                        chartHeight={section.id === 'orders' && showOrdersDailyRates ? 135 : 155}
                        dense
                        hideDataPoints
                        yAxisLabel=""
                        formatValueAsInteger
                        fixedYDomain={section.id === 'orders' && showOrdersDailyRates ? { min: 0, max: 100 } : null}
                        gradientFillSeriesName={
                          section.id === 'orders' && showOrdersDailyRates
                            ? (ordersRateMode === 'delivery' ? 'Delivery rate' : 'Collection rate')
                            : section.id === 'fuel'
                              ? 'Fill-ups'
                              : 'Score'
                        }
                        color={
                          section.id === 'orders' && showOrdersDailyRates
                            ? (ordersRateMode === 'delivery'
                              ? ORDERS_RATE_LINE_DELIVERY
                              : ORDERS_RATE_LINE_COLLECTION)
                            : section.id === 'fuel'
                              ? '#fb923c'
                              : section.score >= 90 ? '#22c55e' : section.score >= 70 ? '#eab308' : '#ef4444'
                        }
                      />
                    </div>
                  ) : (
                    <div className="h-44 flex items-center justify-center px-3 text-center text-xs text-gray-500">
                      {section.id === 'orders' && showOrdersDailyRates
                        ? (ordersDailyLoading
                          ? (
                            <span className="inline-flex items-center gap-2 text-gray-400">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Loading rates…
                            </span>
                          )
                          : ordersDailyError
                            ? 'Could not load rates — see Key Insights.'
                            : 'No consignment rates in the last 7 days yet.')
                        : section.id === 'fuel'
                          ? 'No daily fill-up history in this period yet.'
                          : 'Collecting daily snapshots — the trend appears once today\'s score is stored.'}
                    </div>
                  )}
                  {section.id === 'orders' && showOrdersDailyRates && (
                    <div className="px-1 mt-0.5 space-y-0.5">
                      {ordersRateDaysOmitted && (
                        <p className="text-xs text-slate-500">
                          Days with no consignments omitted — line joins adjacent days only.
                        </p>
                      )}
                      {ordersRateMode === 'collection' && collectionsProvisional && (
                        <p className="text-xs text-amber-400/90">
                          Collection dates are provisional (entered date).
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
          </div>
        </div>
      </div>
  );
}
